import type { ReactElement } from 'react';
import { Button, Modal } from '@xdocs/ui';
import { useUploadQueue } from './useUploadQueue.js';
import { UploadDropzone } from './UploadDropzone.js';
import { UploadQueueList } from './UploadQueueList.js';
import { useUploadsT } from './useUploadsT.js';
import styles from './UploadModal.module.css';

export interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  /** Explorer folder the upload targets; null = root. */
  folderId: string | null;
}

/**
 * F18 upload surface — drag-drop + picker, client-side pre-validation,
 * per-file progress, then live ingest status (§0.2/E19 all four states per
 * row: queued/uploading/ready/failed). Reused as-is from `/explorer`'s
 * toolbar and (per F1's chat-attach source chips, F-Echat) any other
 * feature that needs to add source files — this modal owns no explorer-
 * specific state, only the upload queue.
 */
export function UploadModal({ open, onClose, folderId }: UploadModalProps): ReactElement {
  const { t } = useUploadsT();
  const { items, addFiles, retry, remove, readyCount, inProgressCount } = useUploadQueue(folderId);

  const summary =
    inProgressCount > 0
      ? t('uploads:summary.inProgress', { count: inProgressCount })
      : readyCount > 0
        ? t('uploads:summary.allReady', { count: readyCount })
        : null;

  return (
    <Modal open={open} onClose={onClose} title={t('uploads:modal.title')} size="md" scrollableBody>
      <div className={styles['content']}>
        <UploadDropzone onFilesSelected={addFiles} />
        <div className={styles['queueSection']}>
          <div className={styles['queueHeader']}>
            <span className={styles['queueTitle']}>{t('uploads:queue.title')}</span>
            {/* Always mounted (not conditionally rendered) so the live region
                itself never unmounts/remounts — only its text content
                changes — which is what makes screen readers reliably
                announce the in-progress/all-ready transition. */}
            <span className={styles['summary']} aria-live="polite">
              {summary ?? ''}
            </span>
          </div>
          <UploadQueueList items={items} onRetry={retry} onRemove={remove} />
        </div>
        <div className={styles['footer']}>
          <Button variant="primary" onClick={onClose}>
            {t('uploads:action.done')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
